import React, { useState, useRef, useCallback, useEffect } from "react";
import MonacoEditor from "@monaco-editor/react";
import { usePermissions } from "../../PermissionsProvider";
import { FinetuningService } from "../../services/FinetuningService";
import { Button } from "../ui/button";
import {
  CheckCircle2Icon,
  XCircleIcon,
  Loader2Icon,
  AlertTriangleIcon,
  RotateCcwIcon,
} from "lucide-react";

interface RewardFunctionEditorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

const RewardFunctionEditor: React.FC<RewardFunctionEditorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const { apiKey } = usePermissions();
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [loadingDefault, setLoadingDefault] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!value) {
      FinetuningService.getDefaultRewardFunction(() => {}, apiKey).subscribe(
        (data: any) => {
          if (data?.code && !value) {
            onChange(data.code);
          }
        },
        () => {}
      );
    }
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleValidate = useCallback(() => {
    if (!value.trim()) return;
    setValidating(true);
    setValidation(null);
    FinetuningService.validateRewardFunction(value, () => {}, apiKey).subscribe(
      (result: ValidationResult) => {
        setValidation(result);
        setValidating(false);
      },
      (err: Error) => {
        setValidation({
          valid: false,
          errors: [`Validation request failed: ${err.message}`],
          warnings: [],
        });
        setValidating(false);
      }
    );
  }, [value, apiKey]);

  const handleResetDefault = () => {
    setLoadingDefault(true);
    setValidation(null);
    FinetuningService.getDefaultRewardFunction(() => {}, apiKey).subscribe(
      (data: any) => {
        if (data?.code) onChange(data.code);
        setLoadingDefault(false);
      },
      () => setLoadingDefault(false)
    );
  };

  const handleEditorChange = (newValue: string | undefined) => {
    const val = newValue || "";
    onChange(val);
    setValidation(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (val.trim()) {
        setValidating(true);
        FinetuningService.validateRewardFunction(
          val,
          () => {},
          apiKey
        ).subscribe(
          (result: ValidationResult) => {
            setValidation(result);
            setValidating(false);
          },
          () => setValidating(false)
        );
      }
    }, 1500);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <label className="text-sm font-gc-med text-foreground">
            Reward Function
          </label>
          <span className="text-xs text-muted-foreground">
            Define how completions are scored during GRPO training
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleResetDefault}
            disabled={disabled || loadingDefault}
            className="text-xs gap-1"
          >
            {loadingDefault ? (
              <Loader2Icon className="w-3 h-3 animate-spin" />
            ) : (
              <RotateCcwIcon className="w-3 h-3" />
            )}
            Reset to Default
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleValidate}
            disabled={disabled || validating || !value.trim()}
            className="text-xs gap-1"
          >
            {validating ? (
              <Loader2Icon className="w-3 h-3 animate-spin" />
            ) : (
              <CheckCircle2Icon className="w-3 h-3" />
            )}
            Validate
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <MonacoEditor
          height={320}
          language="python"
          theme="vs-dark"
          value={value}
          onChange={handleEditorChange}
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            lineNumbers: "on",
            scrollBeyondLastLine: false,
            wordWrap: "on",
            tabSize: 4,
            insertSpaces: true,
            automaticLayout: true,
            readOnly: disabled,
            padding: { top: 16, bottom: 16 },
          }}
        />
      </div>

      {validating && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2Icon className="w-3 h-3 animate-spin" />
          Validating...
        </div>
      )}

      {validation && !validating && (
        <div className="space-y-2">
          {validation.valid ? (
            <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
              <CheckCircle2Icon className="w-4 h-4" />
              Reward function is valid and passes test execution
            </div>
          ) : (
            <div className="space-y-1">
              {validation.errors.map((err, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 text-xs text-red-600 dark:text-red-400"
                >
                  <XCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span className="font-mono">{err}</span>
                </div>
              ))}
            </div>
          )}
          {validation.warnings.map((warn, i) => (
            <div
              key={i}
              className="flex items-start gap-2 text-xs text-yellow-600 dark:text-yellow-400"
            >
              <AlertTriangleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{warn}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RewardFunctionEditor;
